import { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Page, Header } from 'components';
import { Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
	ooImportFile,
	ooGetLastUpdate,
	ooCompletionOverdue,
	ooEndingToday,
	ooPartnerRestricted,
	ooPartnerRestrictedBlacklist,
	ooPartnersEmails,
} from '../actions/oldOutagesThunks';
import './OldOutages.css';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	},
}));

// ─── ICONS ────────────────────────────────────────────────────────────────────
const Icon = {
	Upload: ({ size = 16, color = 'currentColor' }) => (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
		</svg>
	),
	Clock: ({ size = 16, color = 'currentColor' }) => (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
		</svg>
	),
	Calendar: ({ size = 16, color = 'currentColor' }) => (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
		</svg>
	),
	Shield: ({ size = 16, color = 'currentColor' }) => (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
		</svg>
	),
	ShieldOff: ({ size = 16, color = 'currentColor' }) => (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<path d="M19.69 14a6.9 6.9 0 0 0 .31-2V5l-8-3-3.16 1.18"/><path d="M4.73 4.73L4 5v7c0 6 8 10 8 10a20.29 20.29 0 0 0 5.62-4.38"/><line x1="1" y1="1" x2="23" y2="23"/>
		</svg>
	),
	Mail: ({ size = 16, color = 'currentColor' }) => (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
		</svg>
	),
	User: ({ size = 13, color = 'currentColor' }) => (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
		</svg>
	),
	Time: ({ size = 13, color = 'currentColor' }) => (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
		</svg>
	),
	X: ({ size = 16, color = 'currentColor' }) => (
		<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
		</svg>
	),
	Zip: () => (
		<svg width="32" height="32" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
			<path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" stroke="#d97706" strokeWidth="1.5"/>
			<polyline points="13 2 13 9 20 9" stroke="#d97706" strokeWidth="1.5"/>
			<line x1="12" y1="12" x2="12" y2="12" stroke="#d97706" strokeWidth="1.5"/>
			<rect x="10" y="11" width="4" height="2" rx="0.5" stroke="#d97706" strokeWidth="1.5"/>
			<rect x="10" y="14" width="4" height="2" rx="0.5" stroke="#d97706" strokeWidth="1.5"/>
			<rect x="10" y="17" width="4" height="2" rx="0.5" stroke="#d97706" strokeWidth="1.5"/>
		</svg>
	),
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const formatTimestamp = (value) => {
	if (!value) return null;
	try {
		return new Date(value).toLocaleString('pt-PT', {
			day: '2-digit', month: '2-digit', year: 'numeric',
			hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
		});
	} catch { return value; }
};

// ─── SPINNER ──────────────────────────────────────────────────────────────────
function Spinner({ dark = false }) {
	return <span className={`oo-spinner${dark ? ' oo-spinner-dark' : ''}`} />;
}

// ─── ACTION BUTTON ────────────────────────────────────────────────────────────
function ActionButton({ iconColor, iconBg, IconComp, label, description, onClick, loading }) {
	return (
		<button className="oo-action-btn" onClick={onClick} disabled={loading}>
			<div className="oo-action-icon" style={{ background: iconBg }}>
				{loading ? <Spinner dark /> : <IconComp size={16} color={iconColor} />}
			</div>
			<span className="oo-action-btn-label">{label}</span>
			{description && <span className="oo-action-btn-desc">{description}</span>}
		</button>
	);
}

// ─── LAST UPDATE ──────────────────────────────────────────────────────────────
function LastUpdate({ user, timestamp }) {
	const formatted = formatTimestamp(timestamp);
	const hasData   = user || formatted;
	const rows = [
		...(user      ? [{ Icon: Icon.User, text: user }]     : []),
		...(formatted ? [{ Icon: Icon.Time, text: formatted }] : []),
	];

	return (
		<div>
			<p className="oo-last-update-label">Last update</p>
			{hasData ? rows.map(({ Icon: Ic, text }, i) => (
				<div key={i} className="oo-last-update-row">
					<span className="oo-last-update-row-icon"><Ic /></span>
					<span className="oo-last-update-row-text">{text}</span>
				</div>
			)) : (
				<p className="oo-no-data">No data available</p>
			)}
		</div>
	);
}

// ─── UPLOAD MODAL ─────────────────────────────────────────────────────────────
function UploadModal({ title, subtitle, onClose, onConfirm, loading }) {
	const [file, setFile]         = useState(null);
	const [dragOver, setDragOver] = useState(false);
	const [error, setError]       = useState('');
	const inputRef                = useRef(null);

	const MAX_SIZE_MB    = 100;
	const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

	const isValid = (f) => f.name.endsWith('.zip') || f.type === 'application/zip' || f.type === 'application/x-zip-compressed';

	const applyFile = (f) => {
		if (!f) return;
		if (!isValid(f)) { setError('Only .zip files are accepted.'); return; }
		if (f.size > MAX_SIZE_BYTES) { setError(`File size exceeds the ${MAX_SIZE_MB}MB limit.`); return; }
		setError(''); setFile(f);
	};

	const handleDrop = useCallback((e) => {
		e.preventDefault(); setDragOver(false); applyFile(e.dataTransfer.files[0]);
	}, []);

	return (
		<div className="oo-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
			<div className="oo-modal">
				<div className="oo-modal-header">
					<div>
						<p className="oo-modal-title">{title}</p>
						{subtitle && <p className="oo-modal-subtitle">{subtitle}</p>}
					</div>
					<button className="oo-close-btn" onClick={onClose}><Icon.X /></button>
				</div>

				<div className="oo-modal-body">
					<div
						className={`oo-dropzone${dragOver ? ' drag-over' : ''}${file ? ' has-file' : ''}`}
						onClick={() => !file && inputRef.current.click()}
						onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
						onDragLeave={() => setDragOver(false)}
						onDrop={handleDrop}
					>
						{file ? (
							<div className="oo-file-info">
								<Icon.Zip />
								<p className="oo-file-name">{file.name}</p>
								<p className="oo-file-size">{(file.size / 1024).toFixed(1)} KB</p>
								<button className="oo-remove-btn" onClick={(e) => { e.stopPropagation(); setFile(null); setError(''); }}>
									Remove
								</button>
							</div>
						) : (
							<>
								<Icon.Upload size={20} color={dragOver ? '#3b82f6' : '#94a3b8'} />
								<p className="oo-dropzone-title">{dragOver ? 'Drop to upload' : 'Drag & drop or click to browse'}</p>
								<p className="oo-dropzone-sub">.zip — max 100MB</p>
							</>
						)}
					</div>
					<input
						ref={inputRef} type="file"
						accept=".zip,application/zip,application/x-zip-compressed"
						onChange={(e) => applyFile(e.target.files[0])}
						style={{ display: 'none' }}
					/>
					{error && <p className="oo-error-msg">{error}</p>}
				</div>

				<div className="oo-modal-footer">
					<button className="oo-btn oo-btn-ghost" onClick={onClose}>Cancel</button>
					<button
						className="oo-btn oo-btn-primary"
						onClick={() => file && onConfirm(file)}
						disabled={!file || loading}
					>
						{loading ? <><Spinner /> Uploading...</> : 'Upload'}
					</button>
				</div>
			</div>
		</div>
	);
}

// ─── SEND EMAILS MODAL ────────────────────────────────────────────────────────
function SendEmailsModal({ title, subtitle, onClose, onConfirm, loading }) {
	return (
		<div className="oo-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
			<div className="oo-modal">
				<div className="oo-modal-header">
					<div>
						<p className="oo-modal-title">{title}</p>
						{subtitle && <p className="oo-modal-subtitle">{subtitle}</p>}
					</div>
					<button className="oo-close-btn" onClick={onClose}><Icon.X /></button>
				</div>

				<div className="oo-modal-body">
					<button className="oo-send-option" onClick={() => onConfirm(true)} disabled={loading}>
						<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
							<div style={{
								width: 34, height: 34, borderRadius: 8, flexShrink: 0,
								background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
							}}>
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<path d="M9 12l2 2 4-4"/><path d="M20.618 5.984A11.955 11.955 0 0 1 12 2.944a11.955 11.955 0 0 1-8.618 3.04A12.02 12.02 0 0 0 3 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9c0-1.052-.12-2.076-.382-3.016z"/>
								</svg>
							</div>
							<div style={{ textAlign: 'left' }}>
								<p className="oo-send-option-label">Test send emails</p>
								<p className="oo-send-option-desc">Sends to test addresses only — no real recipients affected</p>
							</div>
						</div>
					</button>

					<button className="oo-send-option" onClick={() => onConfirm(false)} disabled={loading}>
						<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
							<div style={{
								width: 34, height: 34, borderRadius: 8, flexShrink: 0,
								background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center',
							}}>
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
									<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
								</svg>
							</div>
							<div style={{ textAlign: 'left' }}>
								<p className="oo-send-option-label">Send final emails</p>
								<p className="oo-send-option-desc">Sends to all real recipients — this action cannot be undone</p>
							</div>
						</div>
					</button>
				</div>

				<div className="oo-modal-footer">
					<button className="oo-btn oo-btn-ghost" onClick={onClose}>Cancel</button>
				</div>
			</div>
		</div>
	);
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
function OldOutages() {
	const classes   = useStyles();
	const dispatch  = useDispatch();
	const loading   = useSelector((state) => state.oldOutagesReducer.loading);
	const lastUpdate = useSelector((state) => state.oldOutagesReducer.lastUpdate);
	const userId    = useSelector((state) => state.session.user?.id);
	const userEmail = useSelector((state) => state.session.user?.email);

	const [modal, setModal] = useState(null); // null | 'import' | 'completionOverdue' | 'endingToday' | 'partnerRestricted' | 'partnerRestrictedBlacklist' | 'partnersEmails'

	useEffect(() => {
		// dispatch(ooGetLastUpdate());
	}, []);

	const handleImport = (file) => {
		dispatch(ooImportFile(userId, file));
		setModal(null);
	};

	const handleSend = (thunk, testEmails) => {
		dispatch(thunk(userId, userEmail, testEmails));
		setModal(null);
	};

	const sendModals = {
		completionOverdue:          { title: 'Completion Overdue',          subtitle: 'Send emails for overdue completions',          thunk: ooCompletionOverdue,          loadingKey: 'completionOverdue' },
		endingToday:                { title: 'Ending Today',                subtitle: 'Send emails for outages ending today',         thunk: ooEndingToday,                loadingKey: 'endingToday' },
		partnerRestricted:          { title: 'Partner Restricted',          subtitle: 'Send emails for partner restricted outages',   thunk: ooPartnerRestricted,          loadingKey: 'partnerRestricted' },
		partnerRestrictedBlacklist: { title: 'Partner Restricted Blacklist', subtitle: 'Send emails for blacklisted partners',        thunk: ooPartnerRestrictedBlacklist, loadingKey: 'partnerRestrictedBlacklist' },
		partnersEmails:             { title: 'Partners Emails',             subtitle: 'Send general partners email notifications',    thunk: ooPartnersEmails,             loadingKey: 'partnersEmails' },
	};

	const activeModal = modal && sendModals[modal];

	return (
		<Page className={classes.root} title="Old Outages">
			<Header module="NASP" view="Old Outages" />
			<Card className={classes.container}>
				<CardContent style={{ padding: 0 }}>
					<div className="oo-root">

						{/* ── Upload Modal ──────────────────────────────────────────── */}
						{modal === 'import' && (
							<UploadModal
								title="Import file"
								subtitle="Only .zip files accepted — max 100MB"
								onClose={() => setModal(null)}
								onConfirm={handleImport}
								loading={loading.importFile}
							/>
						)}

						{/* ── Send Emails Modals ────────────────────────────────────── */}
						{activeModal && (
							<SendEmailsModal
								title={activeModal.title}
								subtitle={activeModal.subtitle}
								onClose={() => setModal(null)}
								onConfirm={(testEmails) => handleSend(activeModal.thunk, testEmails)}
								loading={loading[activeModal.loadingKey]}
							/>
						)}

						<div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

							{/* ── File Importer ─────────────────────────────────────── */}
							<div className="oo-card">
								<div className="oo-card-header">
									<span className="oo-card-title">File Importer</span>
									<span className="oo-badge" style={{ background: '#eff6ff', color: '#1d4ed8' }}>Import</span>
								</div>
								<div className="oo-card-body">
									<div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 220px', gap: '20px', alignItems: 'start' }}>
										<div>
											<ActionButton
												iconColor="#1d4ed8" iconBg="#eff6ff" IconComp={Icon.Upload}
												label="Files Importer"
												description="Upload a new .zip file"
												onClick={() => setModal('import')}
												loading={loading.importFile}
											/>
										</div>

										<div style={{ background: '#f1f5f9', width: 1, alignSelf: 'stretch' }} />

										<LastUpdate
											user={lastUpdate.user}
											timestamp={lastUpdate.timestamp}
										/>
									</div>
								</div>
							</div>

							{/* ── Old Outages ───────────────────────────────────────── */}
							<div className="oo-card">
								<div className="oo-card-header">
									<span className="oo-card-title">Old Outages</span>
									<span className="oo-badge" style={{ background: '#fef9c3', color: '#854d0e' }}>Active</span>
								</div>
								<div className="oo-card-body">
									<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
										<ActionButton
											iconColor="#dc2626" iconBg="#fef2f2" IconComp={Icon.Clock}
											label="Completion Overdue"
											description="Send overdue emails"
											onClick={() => setModal('completionOverdue')}
											loading={loading.completionOverdue}
										/>
										<ActionButton
											iconColor="#0369a1" iconBg="#f0f9ff" IconComp={Icon.Calendar}
											label="Ending Today"
											description="Send ending today emails"
											onClick={() => setModal('endingToday')}
											loading={loading.endingToday}
										/>
										<ActionButton
											iconColor="#7c3aed" iconBg="#faf5ff" IconComp={Icon.Shield}
											label="Partner Restricted"
											description="Send restricted partner emails"
											onClick={() => setModal('partnerRestricted')}
											loading={loading.partnerRestricted}
										/>
										<ActionButton
											iconColor="#b45309" iconBg="#fffbeb" IconComp={Icon.ShieldOff}
											label="Partner Restricted Blacklist"
											description="Send blacklist emails"
											onClick={() => setModal('partnerRestrictedBlacklist')}
											loading={loading.partnerRestrictedBlacklist}
										/>
										<ActionButton
											iconColor="#0f766e" iconBg="#f0fdfa" IconComp={Icon.Mail}
											label="Partners Emails"
											description="Send partner notifications"
											onClick={() => setModal('partnersEmails')}
											loading={loading.partnersEmails}
										/>
									</div>
								</div>
							</div>

						</div>
					</div>
				</CardContent>
			</Card>
		</Page>
	);
}

export default OldOutages;